import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { EAIClient } from "Services/EAIClient";
import { SavedViewListItem, useSavedViewMembers } from "@/hooks/saved-views";
import { useToast } from "@/hooks/useToast";
import { SelectWrapper } from "Components/Common/SelectWrapper";
import { StyledChip } from "Components/Common/StyledChip";
import { User, useUsers } from "@/hooks/user";

/** Pulls a human-friendly message out of an axios error, with a fallback. */
export function errorMessage(e: any): string {
    return (
        e?.response?.data?.result?.message ||
        e?.message ||
        "Something went wrong. Please try again."
    );
}

interface RenameSavedViewModalProps {
    view: SavedViewListItem | null;
    onConfirm: (newName: string) => Promise<void>;
    onClose: () => void;
}

/** Small modal to rename a saved view, pre-filled with the current name. */
export function RenameSavedViewModal({ view, onConfirm, onClose }: RenameSavedViewModalProps) {
    const [name, setName] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (view) {
            setName(view.name);
            setError(null);
            setIsSaving(false);
        }
    }, [view]);

    const trimmedName = name.trim();
    const canSave = trimmedName.length > 0 && !isSaving;

    const handleSave = async () => {
        if (!canSave) return;
        setIsSaving(true);
        setError(null);
        try {
            await onConfirm(trimmedName);
            onClose();
        } catch (e: any) {
            setError(errorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={view !== null} onHide={onClose} centered backdrop={isSaving ? "static" : true}>
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold" style={{ lineHeight: "36px", fontSize: "24px" }}>
                    Rename View
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form
                    id="rename-view-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSave();
                    }}
                >
                    <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            data-test-id="rename-view-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isSaving}
                            autoFocus
                            required
                        />
                    </Form.Group>
                    {error && (
                        <p className="text-danger mt-2 mb-0" data-test-id="rename-view-error">
                            {error}
                        </p>
                    )}
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" style={{ width: "47%" }} onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    style={{ marginLeft: 15, width: "46%" }}
                    onClick={handleSave}
                    disabled={!canSave}
                    data-test-id="rename-view-confirm-btn"
                >
                    {isSaving ? <Spinner animation="border" size="sm" /> : "Save"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

interface RemoveSavedViewModalProps {
    view: SavedViewListItem | null;
    onConfirm: () => Promise<void>;
    onClose: () => void;
}

/** Confirmation modal for removing a saved view from the user's list. */
export function RemoveSavedViewModal({ view, onConfirm, onClose }: RemoveSavedViewModalProps) {
    const [isRemoving, setIsRemoving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (view) {
            setError(null);
            setIsRemoving(false);
        }
    }, [view]);

    const handleRemove = async () => {
        setIsRemoving(true);
        setError(null);
        try {
            await onConfirm();
            onClose();
        } catch (e: any) {
            setError(errorMessage(e));
        } finally {
            setIsRemoving(false);
        }
    };

    return (
        <Modal show={view !== null} onHide={onClose} centered backdrop={isRemoving ? "static" : true}>
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold" style={{ lineHeight: "36px", fontSize: "24px" }}>
                    Remove View
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                Remove this view from your list?
                {error && (
                    <p className="text-danger mt-2 mb-0" data-test-id="remove-view-error">
                        {error}
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" style={{ width: "47%" }} onClick={onClose} disabled={isRemoving}>
                    Cancel
                </Button>
                <Button
                    variant="danger"
                    style={{ marginLeft: 15, width: "46%" }}
                    onClick={handleRemove}
                    disabled={isRemoving}
                    data-test-id="remove-view-confirm-btn"
                >
                    {isRemoving ? <Spinner animation="border" size="sm" /> : "Remove"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

interface ShareSavedViewModalProps {
    view: SavedViewListItem;
    onClose: () => void;
}

/**
 * Modal to share a saved view with colleagues in the same school. A typeahead
 * search adds recipients (shown as dismissible chips below); users who already
 * have access are excluded from the search.
 */
export function ShareSavedViewModal({ view, onClose }: ShareSavedViewModalProps) {
    const { toast } = useToast();
    const client = useMemo(() => new EAIClient(), []);
    const { data: allUsers } = useUsers();
    const { data: members } = useSavedViewMembers(view.ruuid);

    const [recipients, setRecipients] = useState<User[]>([]);
    const [isSharing, setIsSharing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Exclude users who already have access (the current user is a member too,
    // so this also excludes self) and anyone already selected.
    const memberRuuids = new Set((members || []).map((m) => m.user_ruuid));
    const selectedRuuids = new Set(recipients.map((r) => r.ruuid));
    const options = (allUsers?.data || []).filter(
        (u) => !memberRuuids.has(u.ruuid) && !selectedRuuids.has(u.ruuid)
    );

    const addRecipient = (user: User | null) => {
        if (user) setRecipients((prev) => [...prev, user]);
    };
    const removeRecipient = (ruuid: string) =>
        setRecipients((prev) => prev.filter((r) => r.ruuid !== ruuid));

    const canShare = recipients.length > 0 && !isSharing;

    const handleShare = async () => {
        if (!canShare) return;
        setIsSharing(true);
        setError(null);
        try {
            await client.shareSavedView(view.ruuid, recipients.map((r) => r.ruuid));
            const count = recipients.length;
            toast({
                type: "success",
                title: "View shared",
                message: `View shared with ${count} ${count === 1 ? "person" : "people"}`,
            });
            onClose();
        } catch (e: any) {
            setError(errorMessage(e));
        } finally {
            setIsSharing(false);
        }
    };

    return (
        <Modal show onHide={onClose} centered backdrop={isSharing ? "static" : true}>
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold" style={{ lineHeight: "36px", fontSize: "24px" }}>
                    Share “{view.name}”
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group data-test-id="share-view-search">
                    <Form.Label>Add people</Form.Label>
                    <SelectWrapper<User>
                        getOptionLabel={(u: User) => u.name}
                        getOptionValue={(u: User) => u.ruuid}
                        options={options}
                        value={null}
                        isDisabled={isSharing}
                        placeholder="Search people…"
                        onChange={(u) => addRecipient(u as User | null)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 1100 }) }}
                    />
                </Form.Group>

                {recipients.length > 0 && (
                    <div className="share-view-recipients" data-test-id="share-view-recipients">
                        {recipients.map((r) => (
                            <StyledChip key={r.ruuid} text={r.name} onClose={() => removeRecipient(r.ruuid)} />
                        ))}
                    </div>
                )}

                {error && (
                    <p className="text-danger mt-2 mb-0" data-test-id="share-view-error">
                        {error}
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" style={{ width: "47%" }} onClick={onClose} disabled={isSharing}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    style={{ marginLeft: 15, width: "46%" }}
                    onClick={handleShare}
                    disabled={!canShare}
                    data-test-id="share-view-confirm-btn"
                >
                    {isSharing ? <Spinner animation="border" size="sm" /> : "Share"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
